<script setup lang="ts">
import type { Popover } from 'primevue';
import { computed, ref } from 'vue';

import { formatIban } from '@peanuds/shared';
import type { UserAccountBankDetails } from '@peanuds/shared-state';

import UiCopyToggleButton from './wrappers/UiCopyToggleButton.vue';
import CopyIcon from '../assets/icons/small-copy.svg?component';

type PrimePopoverInstance = InstanceType<typeof Popover>;

/**
 * How to use in Parent:
 *
 * import { SharePopover, UiButton, useSharePopover } from '@peanuds/ui';
 *
 * const { sharePopoverRef, toggleShare } = useSharePopover();
 *
 * <SharePopover ref="sharePopoverRef" />
 */

interface Props {
  bankDetails?: UserAccountBankDetails | null;
}

const { bankDetails = null } = defineProps<Props>();

const popoverRef = ref<PrimePopoverInstance | null>(null);
const isPopoverVisible = ref(false);

const toggle = (event: MouseEvent) => {
  const popover = popoverRef.value;
  if (!popover) return;

  if (isPopoverVisible.value) {
    popover.hide();
    isPopoverVisible.value = false;
    return;
  }

  popover.show(event as unknown as Event);
  isPopoverVisible.value = true;
};

const hide = () => {
  popoverRef.value?.hide();
  isPopoverVisible.value = false;
};

const isVisible = () => isPopoverVisible.value;

const handleHide = () => {
  isPopoverVisible.value = false;
};

defineExpose({
  toggle,
  hide,
  isVisible,
});

const hasValue = (value: string | null | undefined) => {
  return typeof value === 'string' && value.trim().length > 0;
};

const visibleAccountDetails = computed(() => {
  const iban = bankDetails?.iban ?? '';
  return [
    {
      key: 'iban',
      label: 'IBAN',
      value: iban,
      displayValue: hasValue(iban) ? formatIban(iban) : '',
      copyText: iban,
      tooltipText: 'IBAN',
    },
    {
      key: 'bankName',
      label: 'Bank Name',
      value: bankDetails?.bankName ?? '',
      displayValue: bankDetails?.bankName ?? '',
      copyText: bankDetails?.bankName ?? '',
      tooltipText: 'Bank Name',
    },
    {
      key: 'bic',
      label: 'BIC',
      value: bankDetails?.bic ?? '',
      displayValue: bankDetails?.bic ?? '',
      copyText: bankDetails?.bic ?? '',
      tooltipText: 'BIC',
    },
    {
      key: 'bankAddress',
      label: 'Bank Address',
      value: bankDetails?.bankAddress ?? '',
      displayValue: bankDetails?.bankAddress ?? '',
      copyText: bankDetails?.bankAddress ?? '',
      tooltipText: 'Bank Address',
    },
    {
      key: 'recipient',
      label: 'Recipient',
      value: bankDetails?.receiver ?? '',
      displayValue: bankDetails?.receiver ?? '',
      copyText: bankDetails?.receiver ?? '',
      tooltipText: 'Recipient',
    },
  ].filter((field) => hasValue(field.value));
});

// Format account details for copying
const formattedAccountDetails = computed(() => {
  return visibleAccountDetails.value.map((field) => `${field.label}: ${field.copyText}`).join('\n');
});

// Copy state management
type CopyState = 'idle' | 'loading' | 'success';
const copyState = ref<CopyState>('idle');

// Copy all information
async function handleCopyAll() {
  if (copyState.value === 'loading') return;
  copyState.value = 'loading';

  try {
    await navigator.clipboard.writeText(formattedAccountDetails.value);
    copyState.value = 'success';
    setTimeout(() => {
      if (copyState.value === 'success') copyState.value = 'idle';
    }, 1000);
  } catch (err) {
    console.warn('Failed to copy account details:', err);
    copyState.value = 'idle';
  }
}

const isLoading = computed(() => copyState.value === 'loading');
const showCopied = computed(() => copyState.value === 'success');

// Tooltip configuration
const tooltipConfig = computed(() => ({
  value: isLoading.value ? 'Loading…' : showCopied.value ? 'Copied' : 'Copy Information',
  showDelay: showCopied.value ? 0 : 2000,
}));
</script>

<template>
  <Popover
    ref="popoverRef"
    @hide="handleHide"
    :pt="{
      root: {
        class: 'share-popover',
      },
    }"
  >
    <div class="account-details-popover">
      <div class="account-details-popover__content">
        <div class="account-details-popover__card">
          <h6 class="account-details-popover__title">SEPA Bank details</h6>

          <div class="account-details-popover__fields">
            <div
              v-for="field in visibleAccountDetails"
              :key="field.key"
              class="account-details-popover__field"
            >
              <div class="account-details-popover__field-content">
                <div class="account-details-popover__field-label">{{ field.label }}</div>
                <div class="account-details-popover__field-value">{{ field.displayValue }}</div>
              </div>
              <div class="account-details-popover__field-action">
                <UiCopyToggleButton
                  :tooltip-text="field.tooltipText"
                  :copy-text="field.copyText ?? ''"
                  icon-dark
                />
              </div>
            </div>
          </div>

          <div
            v-if="visibleAccountDetails.length > 0"
            :key="tooltipConfig.value"
            v-tooltip.top="tooltipConfig"
            class="account-details-popover__copy-all-wrapper"
          >
            <UiButton
              label="Copy Information"
              severity="primary"
              size="s"
              iconPos="right"
              :disabled="isLoading"
              class="account-details-popover__copy-all-button"
              @click="handleCopyAll"
            >
              <template #icon>
                <CopyIcon />
              </template>
            </UiButton>
          </div>
        </div>
      </div>
    </div>
  </Popover>
</template>

<style lang="scss">
.share-popover {
  border-radius: var(--radius-16);

  .p-popover-content {
    padding: 0;
  }
}
</style>

<style lang="scss" scoped>
.account-details-popover {
  &__content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__card {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: $static-space-6;
    background-color: transparent;
    border-radius: var(--p-border-radius-xl); /* 12px */
    width: 21.875rem; /* 350px */
  }

  &__title {
    font-family: var(--font-family-base);
    font-size: 1.125rem; /* 18px */
    font-weight: $font-weight-semibold;
    line-height: 100%;
    letter-spacing: var(--p-letter-spacing-tight-2);
    color: var(--p-text-color, #334155);
    margin-bottom: 1.25rem;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 0.75rem;
  }

  &__field {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
  }

  &__field-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: space-between;
  }

  &__field-label {
    font-family: var(--font-family-base);
    font-size: 0.75rem; /* 12px */
    font-weight: $font-weight-semibold;
    line-height: 100%;
    letter-spacing: -0.048px;
    color: var(--p-text-color, #334155);
    // margin-bottom: 0.25rem;
    white-space: nowrap;
  }

  &__field-value {
    font-family: var(--font-family-base);
    font-size: 1rem; /* 16px */
    font-weight: $font-weight-regular;
    line-height: $line-height-md;
    color: var(--p-text-color, #334155);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__field-action {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &__copy-all-wrapper {
    align-self: flex-end;
  }

  &__copy-all-button {
    height: 1.625rem; /* 26px */
    font-size: 0.75rem; /* 12px */
    font-weight: $font-weight-medium;
    font-family: var(--font-family-base);
    letter-spacing: -0.048px;
    padding: 0.390625rem 0.609375rem; /* 6.25px 9.75px */
  }
}
</style>
